<template>
  <div class="border border-gray-300 rounded-md overflow-hidden">
    <div v-if="editor" class="flex items-center gap-1 px-2 py-1 border-b border-gray-200 bg-gray-50 text-xs">
      <button
        type="button"
        :class="['btn btn-ghost btn-xs', { 'bg-gray-300': editor.isActive('bold') }]"
        title="Bold"
        @click="editor.chain().focus().toggleBold().run()">
        <strong>B</strong>
      </button>
      <button
        type="button"
        :class="['btn btn-ghost btn-xs italic', { 'bg-gray-300': editor.isActive('italic') }]"
        title="Italic"
        @click="editor.chain().focus().toggleItalic().run()">
        I
      </button>
      <span class="w-px h-4 bg-gray-300 mx-1" />
      <button
        type="button"
        class="btn btn-ghost btn-icon btn-xs"
        title="Add link"
        @click="setLink">
        <EditorIcon name="link" class="w-3.5 h-3.5" />
      </button>
      <button
        v-if="editor.isActive('link')"
        type="button"
        class="btn btn-ghost btn-icon btn-xs"
        title="Remove link"
        @click="editor.chain().focus().unsetLink().run()">
        <EditorIcon name="link-off" class="w-3.5 h-3.5" />
      </button>
      <span class="w-px h-4 bg-gray-300 mx-1" />
      <button
        type="button"
        class="btn btn-ghost btn-icon btn-xs"
        title="Clear formatting"
        @click="clearFormatting">
        <EditorIcon name="clear-format" class="w-3.5 h-3.5" />
      </button>
    </div>
    <div ref="editorElement" class="tiptap-template-content prose-sm max-w-none p-2 min-h-[80px] focus-within:bg-gray-50" />
  </div>
</template>

<script setup>
import { onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { Editor } from '@tiptap/vue-3'
import StarterKit from '@tiptap/starter-kit'
import Italic from '@tiptap/extension-italic'
import Link from '@tiptap/extension-link'
import EditorIcon from './EditorIcon.vue'

// Templates highlight a word inside a heading either with <em> (e.g. Linden)
// or with <span class="italic" style="color:var(--accent)"> (e.g. Vela). The
// stock italic mark only parses <em>/<i>, so the span form was being flattened
// to plain text on edit — silently dropping the template's accent highlight.
// Extend italic to also parse those spans; it always re-serializes to <em>,
// and each template's CSS styles <em> with its own accent treatment.
const AccentItalic = Italic.extend({
  parseHTML() {
    return [
      ...(this.parent?.() || []),
      { tag: 'span.italic' },
      { tag: 'span[style*="--accent"]' }
    ]
  }
})

// Inline rich-text editor for SiteTemplateSection schema fields with
// type: 'richtext'. Stores HTML (not TipTap JSON) so the renderer can
// pipe the value through `inner_html =` as before.
//
// The toolbar is intentionally minimal — bold, italic, link — to fit
// the small inline forms in the section sidebar. Heading + list nodes
// from StarterKit stay enabled in the editor but aren't surfaced in
// the toolbar.

const props = defineProps({
  modelValue: { type: String, default: '' }
})

const emit = defineEmits(['update:modelValue'])

const editorElement = ref(null)
const editor = ref(null)

onMounted(() => {
  editor.value = new Editor({
    element: editorElement.value,
    content: props.modelValue || '',
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3]},
        italic: false,
        // StarterKit v3 bundles its own Link; disable it so our configured
        // Link (below) is the single source — avoids the duplicate-extension
        // warning and guarantees openOnClick:false in the editor.
        link: false
      }),
      AccentItalic,
      Link.configure({ openOnClick: false, autolink: false })
    ],
    editorProps: {
      // Strip presentational attributes (inline styles, classes) from pasted
      // HTML so a paste from Word / Google Docs / an AI draft can't smuggle in
      // font-weight (which TipTap's Bold mark would adopt) or other styling
      // the template owns. Structure + intentional <strong>/<em>/links survive.
      transformPastedHTML: (html) => html.replace(/\s(?:style|class)="[^"]*"/gi, '')
    },
    onBlur: ({ editor: e }) => {
      const html = e.getHTML()
      const next = isEmptyHtml(html) ? '' : html
      // Only emit on a real change — a no-op blur must not save + reload.
      if (next !== (props.modelValue || '')) emit('update:modelValue', next)
    }
  })
})

onBeforeUnmount(() => {
  editor.value?.destroy()
})

watch(() => props.modelValue, (next) => {
  if (!editor.value) return
  const current = editor.value.getHTML()
  // Avoid resetting the editor cursor on outside-source updates.
  if (next === current) return
  editor.value.commands.setContent(next || '', false)
})

function isEmptyHtml(html) {
  return /^\s*(<p>\s*<\/p>)?\s*$/i.test(html)
}

// Remove all inline marks (bold, italic, link) AND lift heading/list nodes
// back to plain paragraphs from the current selection — or the whole doc when
// nothing is selected. The escape hatch for content that arrived with stray
// formatting (e.g. a legacy save or a paste) the user wants gone.
function clearFormatting() {
  const chain = editor.value.chain().focus()
  const { empty } = editor.value.state.selection
  if (empty) chain.selectAll()
  chain.unsetAllMarks().clearNodes().run()
}

function setLink() {
  const previous = editor.value.getAttributes('link').href || ''
  const url = window.prompt('Link URL', previous)
  if (url === null) return
  if (url === '') {
    editor.value.chain().focus().unsetLink().run()
    return
  }
  editor.value.chain().focus().extendMarkRange('link').setLink({ href: url })
    .run()
}
</script>
